import React, { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Card, CardBody, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import IconsContext from '../../Context/IconsContext';
import { MAINTENANCE_LOG_ID } from '../../Constants/StaticUUIDs';

export default memo(ServiceCompleteCard);

ServiceCompleteCard.propTypes = {
  assetShadowSignalsMap: PropTypes.instanceOf(Map),
  instance: PropTypes.shape({
    id: PropTypes.string,
    due: PropTypes.bool,
    dueOn: PropTypes.string,
    reminder: PropTypes.shape({
      name: PropTypes.string,
      recurring: PropTypes.bool,
      trigger: PropTypes.shape({
        frequency: PropTypes.string,
        interval: PropTypes.number,
        name: PropTypes.string,
        type: PropTypes.string,
      }),
    }),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  now: PropTypes.object,
  processing: PropTypes.oneOf([true, false, 'complete']),
  resetReminder: PropTypes.bool,
};
ServiceCompleteCard.defaultProps = {
  assetShadowSignalsMap: new Map(),
  instance: {
    reminder: {
      name: '',
      trigger: { type: '' },
    },
  },
  processing: false,
};

const frequencyDisplayTextMap = new Map([
  ['weekly', 'week'],
  ['monthly', 'month'],
  ['yearly', 'year'],
]);

function ServiceCompleteCard({
  assetShadowSignalsMap,
  instance,
  now,
  processing,
  resetReminder,
}) {
  const { assetIcon } = useContext(IconsContext);
  const {
    reminder: {
      recurring,
      trigger: { frequency, interval, name, type, value: triggerValue } = {},
    } = {},
    value,
  } = instance;
  const { value: signalValue = 0 } = assetShadowSignalsMap.get(name) ?? {};
  const roundedValue = Math.round((signalValue + Number.EPSILON) * 100) / 100;
  const overdueDisplayValue =
    Math.round((signalValue - value + Number.EPSILON) * 100) / 100;
  const upcomingDisplayValue =
    Math.round((value - signalValue + Number.EPSILON) * 100) / 100;
  const nextServiceValue =
    (resetReminder ? Math.round(signalValue) : value) + triggerValue;

  return (
    <Card className="mb-3 rounded-lg">
      <CardBody className="container-fluid">
        <div className="row">
          <div className="col d-flex align-items-center">
            <img src={assetIcon} alt="assetImage" style={{ height: '25px' }} />
            <div className="flex-fill ml-2 ml-lg-3">
              <ReminderDetails
                assetShadowSignalsMap={assetShadowSignalsMap}
                instance={instance}
                now={now}
                overdueDisplayValue={overdueDisplayValue}
                roundedValue={roundedValue}
                signalValue={signalValue}
                upcomingDisplayValue={upcomingDisplayValue}
              />
            </div>
            {(() => {
              switch (processing) {
                case true:
                  return (
                    <FontAwesomeIcon
                      className="text-danger"
                      icon={faCircleNotch}
                      size="lg"
                      spin
                    />
                  );
                case 'complete':
                  return (
                    <FontAwesomeIcon
                      className="text-alert-confirmation"
                      icon={faCheck}
                      size="lg"
                    />
                  );
                case false:
                  return null;
              }
            })()}
          </div>
        </div>
        <div className="row">
          <div className="col">
            <hr />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-6 d-flex align-items-center">
            {recurring ? (
              <>
                <span className="nine-tenth-text text-text-light-gray mr-2">
                  {`Next Maintenance due ${
                    type === 'conditional' ? 'at' : 'in'
                  }:`}
                </span>
                <span className="normal-text text-muted font-weight-bold mr-2">
                  {type === 'conditional'
                    ? `${nextServiceValue || ''}`
                    : `${interval}`}
                </span>
                <span className="nine-tenth-text text-text-light-gray">
                  {type === 'conditional'
                    ? 'hours/cycles'
                    : `${frequencyDisplayTextMap.get(frequency) ?? ''}`}
                </span>
              </>
            ) : (
              <span className="nine-tenth-text text-text-light-gray">
                Non-recurring Reminder. No future maintenance scheduled.
              </span>
            )}
          </div>
          <div className="col-12 col-lg-6 d-flex align-items-center justify-content-lg-end mt-2 mt-lg-0">
            <span className="nine-tenth-text text-text-light-gray mr-2">
              Service Complete Date:
            </span>
            <span className="nine-tenth-text text-muted font-weight-bold">
              {now ? now.format('MMMM Do, Y') : ''}
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

const iconDisplayMap = new Map([
  ['upcoming', { color: 'warning-yellow' }],
  ['due', { color: 'active-dm' }],
  ['complete', { color: 'alert-confirmation' }],
]);

const { className: progressClass, styles: progressStyles } = css.resolve`
  div.progress {
    height: 0.5rem;
  }
`;

function ReminderDetails(props) {
  const {
    instance: {
      due,
      dueOn,
      maintenanceLogId,
      reminder: {
        name: reminderName = '',
        recurring,
        trigger: { frequency, interval, type } = {},
      } = {},
      value,
    },
    now,
    overdueDisplayValue,
    roundedValue,
    signalValue,
    upcomingDisplayValue,
  } = props;
  const instanceType = useMemo(() => {
    switch (true) {
      case !!maintenanceLogId && maintenanceLogId !== MAINTENANCE_LOG_ID:
        return 'complete';
      case !due:
        return 'upcoming';
      case due &&
        (!maintenanceLogId || maintenanceLogId === MAINTENANCE_LOG_ID):
        return 'due';
    }
  }, []);
  const { color } = iconDisplayMap.get(instanceType) ?? {};

  const dueTime = moment(dueOn);
  const timeDiff = now.diff(dueTime, 'days');
  const timeDiffDisplayText =
    instanceType === 'due' && timeDiff ? 'Days Overdue' : 'Days Left';
  const recurringDisplayText = recurring ? 'Every' : 'Once at';
  const frequencyDisplayText = frequencyDisplayTextMap.get(frequency);

  return type === 'conditional' ? (
    <div className="py-2 px-4">
      <p className="mb-0 three-quarter-text text-dark">{reminderName ?? ''}</p>
      {instanceType === 'complete' ? (
        <p className={`mt-2 mb-0 text-${color}`}>Service Completed</p>
      ) : (
        <Progress
          className={`w-100 my-1 ${progressClass}`}
          color={color}
          value={instanceType === 'due' ? 100 : (signalValue / value) * 100}
        />
      )}
      <span
        className={
          instanceType === 'complete'
            ? 'd-none'
            : 'w-100 d-flex justify-content-between'
        }
      >
        <span className={`text-${color} three-quarter-text`}>
          {instanceType === 'due'
            ? `${overdueDisplayValue} hours overdue`
            : `${upcomingDisplayValue} hours remaining`}
        </span>
        <span
          className={
            instanceType === 'upcoming'
              ? 'd-none'
              : `text-${color} three-quarter-text`
          }
        >
          {roundedValue} Total Hours
        </span>
      </span>
      {progressStyles}
    </div>
  ) : (
    <div className="row no-gutters px-2 px-lg-4 py-2">
      <div className="col-8 py-1 pr-1">
        <p className="mb-1 nine-tenth-text text-dark text-truncate">
          {reminderName ?? ''}
        </p>
        <p className="mb-0 three-quarter-text text-text-light-gray">
          {`${recurringDisplayText ?? ''} ${interval > 1 ? interval : ''} ${
            interval > 1 ? `${frequencyDisplayText}s` : frequencyDisplayText
          }`}
        </p>
      </div>
      <div className="col-4 d-flex flex-column">
        <div
          className={`flex-fill p-2 rounded-lg border border-${color} d-flex flex-column align-items-center justify-content-around`}
        >
          {instanceType === 'complete' ? (
            <p className={`text-${color} three-quarter-text`}>
              Service Complete
            </p>
          ) : (
            <>
              <p className={`mb-0 text-${color}`}>{Math.abs(timeDiff)}</p>
              <p className="three-quarter-text mb-0 text-text-light-gray text-center text-nowrap">
                {timeDiffDisplayText}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

ReminderDetails.propTypes = {
  assetShadowSignalsMap: PropTypes.object,
  instance: PropTypes.object,
  now: PropTypes.object,
  overdueDisplayValue: PropTypes.number,
  roundedValue: PropTypes.number,
  signalValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  upcomingDisplayValue: PropTypes.number,
};

ReminderDetails.defaultProps = {
  assetShadowSignalsMap: new Map(),
  now: moment(),
};
